import {useCallback, useEffect, useState} from 'react';
import { useAuth, useMirrorUser } from '@/lib/firebase';
import { generateTryOn, TOKENS_PER_GENERATION } from '@/lib/tryon';
import { Link } from 'react-router-dom';

const LOADING_MESSAGES = [
  'Stitching seams…',
  'Ironing creases…',
  'Measuring hems…',
  'Threading needles…',
  'Pressing fabric…',
  'Fitting the fit…',
  'Adding finishing touches…',
  'Tailoring to you…',
  'Almost there…',
];

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please choose an image file'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/** Fetch an image URL and return as data URL. Fails if the server does not allow CORS. */
function fetchUrlAsDataUrl(url: string): Promise<string> {
  return fetch(url, { mode: 'cors' })
    .then((res) => {
      if (!res.ok) throw new Error(`Failed to load image (${res.status})`);
      return res.blob();
    })
    .then((blob) => {
      if (!blob.type.startsWith('image/')) throw new Error('URL is not an image');
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read image'));
        reader.readAsDataURL(blob);
      });
    });
}

/** Get image URL from drop data when browser doesn't provide a File (e.g. drag from another tab). */
function getImageUrlFromDrop(dataTransfer: DataTransfer): string | null {
  const uriList = dataTransfer.getData('text/uri-list');
  if (uriList) {
    const first = uriList.trim().split(/[\r\n]+/)[0];
    if (first && /^https?:\/\//i.test(first)) return first;
  }
  const html = dataTransfer.getData('text/html');
  if (html) {
    const match = /<img[^>]+src=["']([^"']+)["']/i.exec(html);
    if (match?.[1] && /^https?:\/\//i.test(match[1])) return match[1];
  }
  return null;
}

function getImageFromClipboard(e: React.ClipboardEvent): File | null {
  const items = e.clipboardData?.items;
  if (!items) return null;
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) return file;
      break;
    }
  }
  return null;
}

type ImageSlotProps = {
  title: string;
  step: number;
  dataUrl: string | null;
  onSelect: (dataUrl: string) => void;
  onClear: () => void;
  disabled?: boolean;
};

function ImageSlot({ title, step, dataUrl, onSelect, onClear, disabled }: ImageSlotProps) {
  const [dragOver, setDragOver] = useState(false);
  const [pasteHint, setPasteHint] = useState(false);

  const handleFile = useCallback(
    async (file: File | null) => {
      if (!file || disabled) return;
      try {
        const url = await readFileAsDataUrl(file);
        onSelect(url);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Could not load image');
      }
    },
    [onSelect, disabled]
  );

  const handleDataUrl = useCallback(
    (dataUrl: string) => {
      if (disabled) return;
      onSelect(dataUrl);
    },
    [onSelect, disabled]
  );

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const file = e.dataTransfer.files?.[0];
      if (file?.type.startsWith('image/')) {
        handleFile(file);
        return;
      }
      const imageUrl = getImageUrlFromDrop(e.dataTransfer);
      if (imageUrl) {
        try {
          const dataUrl = await fetchUrlAsDataUrl(imageUrl);
          handleDataUrl(dataUrl);
        } catch (err) {
          alert(
            "Couldn't use that image from the other page (the site may block it). Try right‑clicking the image → Copy image, then paste here with Ctrl+V."
          );
        }
        return;
      }
      if (e.dataTransfer.types.includes('Files')) {
        alert('Please drop an image file (e.g. JPG or PNG).');
      }
    },
    [handleFile, handleDataUrl, disabled]
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const file = getImageFromClipboard(e);
      if (file) {
        e.preventDefault();
        handleFile(file);
      }
    },
    [handleFile]
  );

  const onFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      handleFile(file ?? null);
      e.target.value = '';
    },
    [handleFile]
  );

  useEffect(() => {
    if (!pasteHint) return;
    const t = setTimeout(() => setPasteHint(false), 2000);
    return () => clearTimeout(t);
  }, [pasteHint]);

  return (
    <section className="rounded-xl border border-mirror-border bg-mirror-card p-4 transition-[border-color] hover:border-[rgba(201,169,98,0.3)]">
      <h2 className="mb-3 flex items-center gap-2 text-[0.9rem] font-semibold text-mirror-text">
        <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[rgba(201,169,98,0.2)] text-[0.75rem] font-bold text-mirror-gold">
          {step}
        </span>
        {title}
      </h2>
      {dataUrl ? (
        <div className="flex flex-col gap-2.5">
          <div className="relative aspect-square min-h-[120px] overflow-hidden rounded-lg border-2 border-solid border-[rgba(201,169,98,0.2)] bg-mirror-surface">
            <img src={dataUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <button
              type="button"
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full border-0 bg-black/60 text-xl leading-none text-white opacity-90 transition hover:opacity-100 hover:bg-[#e57373]"
              onClick={onClear}
              title="Remove"
            >
              ×
            </button>
          </div>
          <button
            type="button"
            className="self-start rounded-lg border border-mirror-border bg-mirror-surface px-4 py-2.5 text-[0.9rem] font-medium text-mirror-text transition-colors hover:bg-mirror-border"
            onClick={onClear}
          >
            Choose different
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={`relative flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-mirror-surface p-5 text-center transition-colors ${
            dragOver ? 'border-mirror-gold bg-[rgba(201,169,98,0.08)]' : 'border-mirror-border'
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
          }}
        >
          <label className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center p-5 text-center hover:bg-white/[0.03]">
            <span className="mb-2 text-3xl opacity-70">📷</span>
            <p className="mb-2 text-[0.8rem] text-mirror-muted">
              Drag & drop, paste (Ctrl+V), or click to choose
            </p>
            <span className="rounded-lg border border-mirror-border bg-mirror-surface px-4 py-2.5 text-[0.9rem] font-medium text-mirror-text">
              Choose image
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileInputChange}
              disabled={disabled}
            />
          </label>
          {pasteHint && (
            <p className="absolute bottom-2 left-0 right-0 text-[0.75rem] text-mirror-gold">
              Paste an image with Ctrl+V (or Cmd+V)
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export default function WebAppGenerator() {
  const { user } = useAuth();
  const { tokens: tokenCount, loading: tokensLoading } = useMirrorUser();
  const [garmentDataUrl, setGarmentDataUrl] = useState<string | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [useLegacyModel, setUseLegacyModel] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canGenerate =
    !!garmentDataUrl &&
    !!photoDataUrl &&
    !!user &&
    tokenCount !== null &&
    !tokensLoading &&
    tokenCount >= TOKENS_PER_GENERATION;

  const handleGenerate = useCallback(async () => {
    if (!garmentDataUrl || !photoDataUrl || !canGenerate) return;
    setError(null);
    setResultImage(null);
    setGenerating(true);
    let msgIndex = 0;
    setLoadingMessage(LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      msgIndex = (msgIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[msgIndex]);
    }, 2500);
    try {
      const { image } = await generateTryOn(
        garmentDataUrl,
        photoDataUrl,
        useLegacyModel
      );
      setResultImage(image);
      // Token count updates automatically via Firestore listener.
      // Notify extension to refresh its balance when user has popup open.
      try {
        document.dispatchEvent(new CustomEvent('shopmi-tokens-purchased'));
        window.dispatchEvent(new CustomEvent('shopmi-tokens-purchased'));
      } catch {
        // ignore
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      clearInterval(interval);
      setGenerating(false);
      setLoadingMessage('');
    }
  }, [garmentDataUrl, photoDataUrl, useLegacyModel, canGenerate]);

  const bestResultsTipsId = 'best-results-tips';

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-display mb-1 text-2xl font-semibold tracking-wide text-mirror-text">
        Virtual try-on
      </h1>
      <p className="mb-6 text-mirror-muted flex items-start gap-2">
        <span>
          Upload a garment image and a photo of yourself. We&apos;ll generate a try-on result (20
          tokens per generation).
        </span>
      </p>
      <span
          className="group/tip relative mt-0.5 shrink-0 cursor-help"
          aria-describedby={bestResultsTipsId}
      >
          <span
              className="inline-flex h-5 px-2 py-1 items-center justify-center rounded-full border border-mirror-border bg-blue-800 text-[0.7rem] font-semibold text-mirror-muted transition-colors hover:border-mirror-gold hover:bg-[rgba(201,169,98,0.1)] hover:text-mirror-gold"
              aria-label="Best results tips"
          >
            How to get the best results
          </span>
          <span
              id={bestResultsTipsId}
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 w-72 -translate-x-1/2 rounded-lg border border-mirror-border bg-mirror-card px-3 py-2.5 text-left text-[0.8rem] leading-snug text-mirror-text shadow-lg opacity-0 transition-opacity duration-150 group-hover/tip:opacity-100"
          >
            <strong className="text-mirror-gold">Best results tips</strong>
            <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-mirror-muted">
              <li>Garment: plain background, full item visible, good lighting</li>
              <li>Your photo: face the camera, full length or upper body, neutral pose</li>
              <li>If the generation isn't providing optimal results, try a different</li>
              <li>photo of yourself.</li>
              <li>Legacy model works best for shirts, dresses, costumes, and some accessories</li>
            </ul>
          </span>
        </span>
      {/* Token balance (live from Firestore) */}
      <div className="my-6 flex flex-wrap items-baseline gap-2 rounded-xl border border-mirror-border bg-mirror-card p-4">
        <span className="text-[0.85rem] text-mirror-muted">Tokens:</span>
        <span className="text-2xl font-bold text-mirror-gold">
          {tokensLoading ? '…' : tokenCount !== null ? tokenCount : '—'}
        </span>
        {tokenCount !== null && tokenCount < TOKENS_PER_GENERATION && (
          <Link
            to="/billing"
            className="text-[0.85rem] text-mirror-gold hover:underline"
          >
            Top up tokens
          </Link>
        )}
      </div>

      <div className="flex flex-col gap-4">
        <ImageSlot
          title="Garment from image"
          step={1}
          dataUrl={garmentDataUrl}
          onSelect={setGarmentDataUrl}
          onClear={() => setGarmentDataUrl(null)}
          disabled={generating}
        />
        <ImageSlot
          title="Your photo"
          step={2}
          dataUrl={photoDataUrl}
          onSelect={setPhotoDataUrl}
          onClear={() => setPhotoDataUrl(null)}
          disabled={generating}
        />
      </div>

      {/* Legacy model toggle */}
      <div className="mb-4 mt-4">
        <label className="flex cursor-pointer select-none items-center gap-2.5">
          <input
            type="checkbox"
            checked={useLegacyModel}
            onChange={(e) => setUseLegacyModel(e.target.checked)}
            className="absolute h-0 w-0 opacity-0"
            disabled={generating}
          />
          <span
            className={`relative inline-block h-5 w-9 shrink-0 rounded-full border transition-colors ${
              useLegacyModel
                ? 'border-mirror-gold bg-[rgba(201,169,98,0.2)]'
                : 'border-mirror-border bg-mirror-surface'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 h-3.5 w-3.5 rounded-full transition-transform ${
                useLegacyModel ? 'translate-x-5 bg-mirror-gold' : 'translate-x-0 bg-mirror-muted'
              }`}
            />
          </span>
          <span className="text-[0.9rem] text-mirror-text">Use Legacy Model</span>
        </label>
        <p className="ml-11 mt-1.5 text-[0.75rem] leading-snug text-mirror-muted">
          Legacy model works best on shirts, dresses, and some accessories.
        </p>
      </div>

      {/* Generate button */}
      <div className="mt-2">
        <button
          type="button"
          disabled={!canGenerate || generating}
          onClick={handleGenerate}
          className="flex w-full items-center justify-center rounded-lg border-0 bg-mirror-gold py-3.5 px-6 text-base font-semibold text-mirror-bg transition-colors hover:bg-mirror-gold-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {generating ? (
            <span className="animate-pulse">{loadingMessage || 'Generating…'}</span>
          ) : (
            <>Try it on ({TOKENS_PER_GENERATION} Tokens)</>
          )}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-mirror-border bg-mirror-card p-4 text-red-300 border-l-4 border-l-red-500/80">
          {error}
        </div>
      )}

      {resultImage && (
        <section className="mt-6 rounded-xl border border-mirror-border bg-mirror-card p-4">
          <h2 className="mb-3 flex items-center gap-2 text-[0.9rem] font-semibold text-mirror-text">
            Result
          </h2>
          <div className="overflow-hidden rounded-lg bg-mirror-surface">
            <img
              src={resultImage}
              alt="Try-on result"
              className="block w-full"
            />
          </div>
          <a
            href={resultImage}
            download="mirror-tryon.png"
            className="mt-3 inline-flex items-center justify-center rounded-lg bg-mirror-gold py-2.5 px-4 text-sm font-medium text-mirror-bg no-underline transition-colors hover:bg-mirror-gold-hover"
          >
            Download image
          </a>
        </section>
      )}
    </div>
  );
}
